/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { TextField, Box } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import PropTypes from "prop-types";
import "./ApplicationDetailsServiceLinks.css";
import { getConfig } from "../../config";

// Helper function to validate URL format
const isValidUrl = (value) => value.startsWith("https://");

// Helper function to validate email format
const isValidEmail = (value) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(value);
};

// Helper function to check portal configuration
const isPortalType = (portalType) => getConfig().REACT_APP_PORTAL === portalType;

// Helper function to safely get nested legal link
const getLegalLink = (legalLinkInfos, language, linkType) => {
  return legalLinkInfos?.[language]?.legalLinks?.[linkType] || "";
};

// Helper function to process document infos by title
const processDocumentsByTitle = (documentInfos, titleMap) => {
  const result = {};
  documentInfos?.forEach((doc) => {
    if (titleMap[doc.title]) {
      result[titleMap[doc.title]] = doc.documentLink;
    }
  });
  return result;
};

function ApplicationDetailsServiceLinks({ postCallTheNewSubmission }) {
  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );
  const currentLanguage = useSelector(
    (state) => state.languageDetails.currentLanguage
  );
  const [customerSupportLink, setCustomerSupportLink] = useState("");
  const [privacyLink, setPrivacyLink] = useState("");
  const [termsServiceLink, setTermsServiceLink] = useState("");
  const [openSoftwareLink, setOpenSoftwareLink] = useState("");
  const [rightToWithdrwalLink, setRightToWithdrwalLink] = useState("");
  const [imprintLink, setImprintLink] = useState("");
  const [errorCustomerSupportLink, setErrorCustomerSupportLink] =
    useState(false);
  const [errorPrivacyLink, setErrorPrivacyLink] = useState(false);
  const [errorTermsServiceLink, setErrorTermsServiceLink] = useState(false);
  const [errorOpenSoftwareLink, setErrorOpenSoftwareLink] = useState(false);
  const [errorRightToWithdrwalLink, setErrorRightToWithdrwalLink] =
    useState(false);
  const [errorImprintLink, setErrorImprintLink] = useState(false);
  
  const isThirdPartyApp = globalThis.window?.location.href.includes("third-party-apps") || false;

  // Helper function to calculate effective language
  const calculateEffectiveLanguage = () => {
    if (currentLanguage) {
      return currentLanguage;
    }
    const sessionLang = sessionStorage.getItem("currentLanguage");
    if (sessionLang) {
      return sessionLang;
    }
    const defaultLang = submissionData?.defaultLanguage || submissionData?.defaultLang;
    if (defaultLang) {
      return defaultLang;
    }
    if (submissionData?.descriptions && !Array.isArray(submissionData.descriptions)) {
      const keys = Object.keys(submissionData.descriptions);
      if (keys.length > 0) {
        return keys[0];
      }
    }
    return null;
  };

  // Helper to check if submission data is valid
  const isValidSubmissionData = () => submissionData && Object.keys(submissionData).length > 0;

  // Helper to get customer support link from descriptions
  const getCustomerSupportFromDescriptions = (descriptions) => {
    const filtered = descriptions.filter((data) => data.language === currentLanguage);
    return filtered.length > 0 && filtered[0].customerSupportLink ? filtered[0].customerSupportLink : "";
  };

  // Helper to set all legal links to empty
  const clearAllLegalLinks = () => {
    setPrivacyLink("");
    setTermsServiceLink("");
    setOpenSoftwareLink("");
    setRightToWithdrwalLink("");
    setImprintLink("");
  };

  // Helper to process seller/global-admin/domain-approver portals
  const processSellerPortalLinks = (effectiveLang) => {
    if (!isValidSubmissionData()) {
      return;
    }

    if (submissionData.descriptions && Array.isArray(submissionData.descriptions)) {
      setCustomerSupportLink(getCustomerSupportFromDescriptions(submissionData.descriptions));
    }

    const legalLinkInfos = submissionData?.legalDocuments?.legalLinkInfos;
    if (legalLinkInfos) {
      setPrivacyLink(getLegalLink(legalLinkInfos, currentLanguage, "Privacy Policy"));
      setTermsServiceLink(getLegalLink(legalLinkInfos, currentLanguage, "Terms of Services"));
      setOpenSoftwareLink(getLegalLink(legalLinkInfos, currentLanguage, "Open Source Software"));
      setRightToWithdrwalLink(getLegalLink(legalLinkInfos, currentLanguage, "Right to Withdrawal"));
      setImprintLink(getLegalLink(legalLinkInfos, currentLanguage, "Imprint"));
    } else {
      clearAllLegalLinks();
    }
  };

  // Helper to process OEM admin portal links
  const processOemAdminPortalLinks = (effectiveLang) => {
    if (!isValidSubmissionData() || !submissionData.descriptions || !effectiveLang) {
      return;
    }

    const descriptions = submissionData.descriptions[effectiveLang];
    if (descriptions?.customerSupportLink) {
      setCustomerSupportLink(descriptions.customerSupportLink);
    }

    const documentInfos = submissionData?.legalDocument?.legalDocumentInfos?.[currentLanguage];
    if (documentInfos && documentInfos.length > 0) {
      const titleMap = {
        "Privacy Policy": "privacy",
        "Terms of Services": "terms",
        "Open Source Software": "oss",
        "Right to Withdrawal": "withdrawal",
        "Imprint": "imprint",
      };
      const processed = processDocumentsByTitle(documentInfos, titleMap);
      setPrivacyLink(processed.privacy || "");
      setTermsServiceLink(processed.terms || "");
      setOpenSoftwareLink(processed.oss || "");
      setRightToWithdrwalLink(processed.withdrawal || "");
      setImprintLink(processed.imprint || "");
    } else {
      clearAllLegalLinks();
    }
  };

  // Helper to process third-party domain approver portal links
  const processThirdPartyPortalLinks = () => {
    if (!isValidSubmissionData() || !submissionData.descriptions) {
      return;
    }

    setCustomerSupportLink(getCustomerSupportFromDescriptions(submissionData.descriptions));

    const documentInfos = submissionData?.legalDocument?.legalDocumentInfos?.[currentLanguage];
    if (documentInfos && documentInfos.length > 0) {
      const titleMap = {
        "Privacy Policy": "privacy",
        "Terms of Services": "terms",
        "Open Source Software": "oss",
        "Right to Withdrawal": "withdrawal",
        "Imprint": "imprint",
      };
      const processed = processDocumentsByTitle(documentInfos, titleMap);
      setPrivacyLink(processed.privacy || "");
      setTermsServiceLink(processed.terms || "");
      setOpenSoftwareLink(processed.oss || "");
      setRightToWithdrwalLink(processed.withdrawal || "");
      setImprintLink(processed.imprint || "");
    }
  };

  useEffect(() => {
    const effectiveLang = calculateEffectiveLanguage();

    if (!isValidSubmissionData()) {
      clearAllLegalLinks();
      return;
    }

    const isSeller = isPortalType("seller");
    const isGlobalAdmin = isPortalType("global-admin");
    const isDomainApprover = isPortalType("domain-approver");

    if ((isSeller || isGlobalAdmin || (isDomainApprover && !isThirdPartyApp))) {
      processSellerPortalLinks(effectiveLang);
    } else if (isPortalType("oem-admin")) {
      processOemAdminPortalLinks(effectiveLang);
    } else if (isDomainApprover && isThirdPartyApp) {
      processThirdPartyPortalLinks();
    }
  }, [currentLanguage, submissionData]);

  // Helper to validate input based on field type
  const validateInput = (value, fieldType) => {
    if (fieldType === 0) { // Customer Support - allows URL or email
      return !isValidUrl(value) && !isValidEmail(value);
    }
    // All other fields require https URL
    return !isValidUrl(value);
  };

  // Field configuration to reduce repeated code
  const fieldConfig = [
    { state: customerSupportLink, setState: setCustomerSupportLink, errorState: errorCustomerSupportLink, setErrorState: setErrorCustomerSupportLink },
    { state: privacyLink, setState: setPrivacyLink, errorState: errorPrivacyLink, setErrorState: setErrorPrivacyLink },
    { state: termsServiceLink, setState: setTermsServiceLink, errorState: errorTermsServiceLink, setErrorState: setErrorTermsServiceLink },
    { state: openSoftwareLink, setState: setOpenSoftwareLink, errorState: errorOpenSoftwareLink, setErrorState: setErrorOpenSoftwareLink },
    { state: rightToWithdrwalLink, setState: setRightToWithdrwalLink, errorState: errorRightToWithdrwalLink, setErrorState: setErrorRightToWithdrwalLink },
    { state: imprintLink, setState: setImprintLink, errorState: errorImprintLink, setErrorState: setErrorImprintLink },
  ];

  const handleChange = ($event, index) => {
    const newValue = $event.target.value;
    const field = fieldConfig[index];
    
    if (field) {
      field.setState(newValue);
      field.setErrorState(validateInput(newValue, index));
    }
  };

  // Helper to handle customer support link submission
  const submitCustomerSupportLink = () => {
    const newDescriptions = [...submissionData.descriptions];
    newDescriptions.forEach((ele) => {
      if (ele.language === currentLanguage) {
        // eslint-disable-next-line no-param-reassign
        ele.customerSupportLink = errorCustomerSupportLink ? null : customerSupportLink;
      }
    });
    const newSubmissionData = {
      ...submissionData,
      descriptions: newDescriptions,
    };
    delete newSubmissionData.legalDocuments;
    postCallTheNewSubmission(newSubmissionData);
  };

  // Helper to create legal document submission object
  const createLegalDocumentSubmission = (title, value, isError) => ({
    legalDocuments: {
      language: currentLanguage,
      legalDocumentTypeInfo: [
        {
          title,
          documentLink: isError ? "" : value,
        },
      ],
    },
  });

  const sendSubmission = (index) => {
    if (index === 0) {
      submitCustomerSupportLink();
      return;
    }

    let obj = {};
    const submissionMap = {
      1: { title: "Privacy Policy", value: privacyLink, hasError: errorPrivacyLink },
      2: { title: "Terms of Services", value: termsServiceLink, hasError: errorTermsServiceLink },
      3: { title: "Open Source Software", value: openSoftwareLink, hasError: errorOpenSoftwareLink },
      4: { title: "Right to Withdrawal", value: rightToWithdrwalLink, hasError: errorRightToWithdrwalLink },
      5: { title: "Imprint", value: imprintLink, hasError: errorImprintLink },
    };

    if (submissionMap[index]) {
      const { title, value, hasError } = submissionMap[index];
      obj = createLegalDocumentSubmission(title, value, hasError);
      postCallTheNewSubmission(obj);
    }
  };
  return (
    <div id="service-links" className="service-links">
      <Box sx={{ width: "100%", marginTop: "25px" }}>
        {submissionData &&
        Object.keys(submissionData).length > 0 &&
        submissionData.status === "DRAFT" ? (
          <div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ flexBasis: "33%" }}>
                <p className="label-text">
                  Customer Support Link/Email Address{" "}
                  {!sessionStorage.getItem("isOemSeller") &&
                    sessionStorage.getItem("isOemSeller") !== "true" && (
                      <span style={{ color: "red" }}>*</span>
                    )}
                </p>
                <TextField
                  sx={{
                    "&.MuiFormControl-root": {
                      width: "300px",
                      padding: "4px 8px 4px 0px",
                      marginTop: "-12px",
                    },
                    "&.MuiFormHelperText-root": {
                      letterSpacing: "0px !important",
                      fontSize: "10px !important",
                      marginLeft: "3px !important",
                    },
                    "&.Mui-error": {
                      letterSpacing: "0px !important",
                      fontSize: "10px !important",
                      marginLeft: "3px !important",
                    },
                  }}
                  label={!customerSupportLink && "https://www.support.com"}
                  
                  slotProps={{
                    formHelperText: {
                      sx: {
                        letterSpacing: "0px !important",
                        fontSize: "10px !important",
                        marginLeft: "3px !important",
                      },
                    },
                    inputLabel: {
                      shrink:false,
                      style: {
                        fontSize: "12px",
                        fontWeight: 400,
                        lineHeight: "16.41px",
                        textAlign: "left",
                        color: "rgba(113, 114, 115, 1)",
                      },
                    },
                  }}
                  value={customerSupportLink}
                  onChange={($event) => handleChange($event, 0)}
                  onBlur={() => sendSubmission(0)}
                  error={errorCustomerSupportLink}
                  helperText={
                    errorCustomerSupportLink
                      ? 'Invalid URL or email format. Please enter a URL starting with "https://". or an email'
                      : ""
                  }
                />
              </div>
              <div style={{ flexBasis: "33%" }}>
                <p className="label-text">
                  Privacy Policy Link
                  {!sessionStorage.getItem("isOemSeller") && (
                    <span style={{ color: "red" }}>*</span>
                  )}
                </p>
                <TextField
                  sx={{
                    "&.MuiFormControl-root": {
                      width: "300px",
                      padding: "4px 8px 4px 0px",
                      marginTop: "-12px",
                    },
                    "&.MuiFormHelperText-root": {
                      letterSpacing: "0px !important",
                      fontSize: "10px !important",
                      marginLeft: "3px !important",
                    },
                    "&.Mui-error": {
                      letterSpacing: "0px !important",
                      fontSize: "10px !important",
                      marginLeft: "3px !important",
                    },
                  }}
                  label={!privacyLink && "https://privacy-url.com"}
                  slotProps={{
                    formHelperText: {
                      sx: {
                        letterSpacing: "0px !important",
                        fontSize: "10px !important",
                        marginLeft: "3px !important",
                      },
                    },
                    inputLabel: {
                      shrink:false,
                      style: {
                        fontSize: "12px",
                        fontWeight: 400,
                        lineHeight: "16.41px",
                        textAlign: "left",
                        color: "rgba(113, 114, 115, 1)",
                      },
                    },
                  }}
                  value={privacyLink}
                  onChange={($event) => handleChange($event, 1)}
                  error={errorPrivacyLink}
                  onBlur={() => sendSubmission(1)}
                  helperText={
                    errorPrivacyLink
                      ? 'Invalid URL format. Please enter a URL starting with "https://".'
                      : ""
                  }
                />
              </div>
              <div style={{ flexBasis: "33%" }}>
                <p className="label-text">Terms Of Service Link</p>
                <TextField
                  sx={{
                    "&.MuiFormControl-root": {
                      width: "300px",
                      padding: "4px 8px 4px 0px",
                      marginTop: "-12px",
                    },
                    "&.MuiFormHelperText-root": {
                      letterSpacing: "0px !important",
                      fontSize: "10px !important",
                      marginLeft: "3px !important",
                    },
                    "&.Mui-error": {
                      letterSpacing: "0px !important",
                      fontSize: "10px !important",
                      marginLeft: "3px !important",
                    },
                  }}
                  label={!termsServiceLink && "https://terms-url.com"}
                  slotProps={{
                    formHelperText: {
                      sx: {
                        letterSpacing: "0px !important",
                        fontSize: "10px !important",
                        marginLeft: "3px !important",
                      },
                    },
                    inputLabel: {
                      shrink:false,
                      style: {
                        fontSize: "12px",
                        fontWeight: 400,
                        lineHeight: "16.41px",
                        textAlign: "left",
                        color: "rgba(113, 114, 115, 1)",
                      },
                    },
                  }}
                  value={termsServiceLink}
                  onChange={($event) => handleChange($event, 2)}
                  onBlur={() => sendSubmission(2)}
                  error={errorTermsServiceLink}
                  helperText={
                    errorTermsServiceLink
                      ? 'Invalid URL format. Please enter a URL starting with "https://".'
                      : ""
                  }
                />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ flexBasis: "33%" }}>
                <p className="label-text">
                  Open Source (OSS) Software Disclosure
                </p>
                <TextField
                  sx={{
                    "&.MuiFormControl-root": {
                      width: "300px",
                      padding: "4px 8px 4px 0px",
                      marginTop: "-12px",
                    },
                    "&.MuiFormHelperText-root": {
                      letterSpacing: "0px !important",
                      fontSize: "10px !important",
                      marginLeft: "3px !important",
                    },
                    "&.Mui-error": {
                      letterSpacing: "0px !important",
                      fontSize: "10px !important",
                      marginLeft: "3px !important",
                    },
                  }}
                  label={!openSoftwareLink && "https://www.harman.com"}
                  slotProps={{
                    formHelperText: {
                      sx: {
                        letterSpacing: "0px !important",
                        fontSize: "10px !important",
                        marginLeft: "3px !important",
                      },
                    },
                    inputLabel: {
                      shrink:false,
                      style: {
                        fontSize: "12px",
                        fontWeight: 400,
                        lineHeight: "16.41px",
                        textAlign: "left",
                        color: "rgba(113, 114, 115, 1)",
                      },
                    },
                  }}
                  value={openSoftwareLink}
                  onChange={($event) => handleChange($event, 3)}
                  onBlur={() => sendSubmission(3)}
                  error={errorOpenSoftwareLink}
                  helperText={
                    errorOpenSoftwareLink
                      ? 'Invalid URL format. Please enter a URL starting with "https://".'
                      : ""
                  }
                />
              </div>
              <div style={{ flexBasis: "33%" }}>
                <p className="label-text">Right To Withdrawl</p>
                <TextField
                  sx={{
                    "&.MuiFormControl-root": {
                      width: "300px",
                      padding: "4px 8px 4px 0px",
                      marginTop: "-12px",
                    },
                    "&.MuiFormHelperText-root": {
                      letterSpacing: "0px !important",
                      fontSize: "10px !important",
                      marginLeft: "3px !important",
                    },
                    "&.Mui-error": {
                      letterSpacing: "0px !important",
                      fontSize: "10px !important",
                      marginLeft: "3px !important",
                    },
                  }}
                  label={!rightToWithdrwalLink && "https://www.harman.com"}
                  slotProps={{
                    formHelperText: {
                      sx: {
                        letterSpacing: "0px !important",
                        fontSize: "10px !important",
                        marginLeft: "3px !important",
                      },
                    },
                    inputLabel: {
                      shrink:false,
                      style: {
                        fontSize: "12px",
                        fontWeight: 400,
                        lineHeight: "16.41px",
                        textAlign: "left",
                        color: "rgba(113, 114, 115, 1)",
                      },
                    },
                  }}
                  value={rightToWithdrwalLink}
                  onChange={($event) => handleChange($event, 4)}
                  onBlur={() => sendSubmission(4)}
                  error={errorRightToWithdrwalLink}
                  helperText={
                    errorRightToWithdrwalLink
                      ? 'Invalid URL format. Please enter a URL starting with "https://".'
                      : ""
                  }
                />
              </div>
              <div style={{ flexBasis: "33%" }}>
                <p className="label-text">Imprint</p>
                <TextField
                  sx={{
                    "&.MuiFormControl-root": {
                      width: "300px",
                      padding: "4px 8px 4px 0px",
                      marginTop: "-12px",
                    },
                    "&.MuiFormHelperText-root": {
                      letterSpacing: "0px !important",
                      fontSize: "10px !important",
                      marginLeft: "3px !important",
                    },
                    "&.Mui-error": {
                      letterSpacing: "0px !important",
                      fontSize: "10px !important",
                      marginLeft: "3px !important",
                    },
                  }}
                  label={!imprintLink && "htpps://www.harman.com"}
                  slotProps={{
                    formHelperText: {
                      sx: {
                        letterSpacing: "0px !important",
                        fontSize: "10px !important",
                        marginLeft: "3px !important",
                      },
                    },
                    inputLabel: {
                      shrink:false,
                      style: {
                        fontSize: "12px",
                        fontWeight: 200,
                        lineHeight: "16.41px",
                        textAlign: "left",
                        color: "rgba(113, 114, 115, 1)",
                      },
                    },
                  }}
                  value={imprintLink}
                  onChange={($event) => handleChange($event, 5)}
                  onBlur={() => sendSubmission(5)}
                  error={errorImprintLink}
                  helperText={
                    errorImprintLink
                      ? 'Invalid URL format. Please enter a URL starting with "https://".'
                      : ""
                  }
                />
              </div>
            </div>
          </div>
        ) : (
          submissionData &&
          Object.keys(submissionData).length > 0 &&
          currentLanguage &&
          currentLanguage.length > 0 && (
            <div
              className="servicelinks-border"
              style={{
                borderBottom: "1px solid rgba(18, 20, 21, 0.2) !important",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  
                  <div>
                    <p className="label-text">
                      Customer Support Link/Email Address
                    </p>
                    <Tooltip title={customerSupportLink}>
                      <p className="link-text">
                        {customerSupportLink || "No URL/Email Address found"}
                      </p>
                    </Tooltip>
                  </div>
                  <div>
                    <p className="label-text">
                      Open Source Software (OSS) Disclosure
                    </p>
                    <Tooltip
                      title={openSoftwareLink}
                    >
                      <p className="link-text">
                        {openSoftwareLink || "No URL found"}
                      </p>
                    </Tooltip>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div>
                    <p className="label-text">Privacy Policy Link </p>
                    <Tooltip title={privacyLink}>
                      <p className="link-text">
                        {privacyLink || "No URL found"}
                      </p>
                    </Tooltip>
                  </div>
                  <div>
                    <p className="label-text">Right To Withdrawl</p>
                    <Tooltip title={rightToWithdrwalLink}>
                      <p className="link-text">
                        {rightToWithdrwalLink || "No URL found"}
                      </p>
                    </Tooltip>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div>
                    <p className="label-text">Terms Of Service Link</p>
                    <Tooltip title={termsServiceLink}>
                      <p className="link-text">
                        {termsServiceLink || "No URL found"}
                      </p>
                    </Tooltip>
                  </div>
                  <div>
                    <p className="label-text">Imprint</p>
                    <Tooltip title={imprintLink}>
                      <p className="link-text">
                        {imprintLink || "No URL found"}
                      </p>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
          )
        )}
      </Box>
     
    </div>
  );
}

ApplicationDetailsServiceLinks.propTypes = {
  postCallTheNewSubmission: PropTypes.func.isRequired,
};

export default ApplicationDetailsServiceLinks;

